import dynamic from 'next/dynamic';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { defineMessages } from 'react-intl';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import IconButtonWithTooltip from '@alltrails/denali/components/IconButtonWithTooltip';
import LinkButtonOrDiv from '@alltrails/denali/components/LinkButtonOrDiv';
import Popover from '@alltrails/denali/components/Popover';
import Overflow from '@alltrails/denali/icons/Overflow';
import { UserConnectionActionType } from '@alltrails/types/belay';
import type SuccessToast from '@alltrails/types/SuccessToast';
import styles from './styles/styles.module.scss';

const UserConnectionOptions = dynamic(() => import('./UserConnectionOptions'), { ssr: false });

const messages = defineMessages({
  REPORT_AN_ISSUE: { defaultMessage: 'Report an issue' },
  MORE_OPTIONS: { defaultMessage: 'More options' },
  RESPOND: { defaultMessage: 'Respond' },
  DELETE: { defaultMessage: 'Delete' },
  EDIT: { defaultMessage: 'Edit' },
  ADMIN: { defaultMessage: 'ADMIN' },
  EXCLUDE_FROM_SUMMARY: { defaultMessage: 'Exclude from summary' },
  ADD_TO_SUMMARY: { defaultMessage: 'Add to summary' }
});

export type TrailReviewOverflowMenuProps = {
  isThirdPartyReview: boolean;
  isMoreOptionsOpen: boolean;
  setIsMoreOptionsOpen: Dispatch<SetStateAction<boolean>>;
  onConnectClick?: (actionType: UserConnectionActionType) => void;
  setIsReportingModalOpen: (isOpen: boolean) => void;
  canComment?: boolean;
  isEditable?: boolean;
  isAdmin?: boolean;
  hideReplies?: boolean;
  onCommentClick?: () => void;
  onDeleteClick?: () => void;
  onEditClick?: () => void;
  setReportingSuccessToast?: Dispatch<SetStateAction<SuccessToast>>;
  summaryExclusion?: boolean;
  onToggleSummaryExclusion?: (summaryExclusion: boolean) => void;
  reviewUserId: number;
};

const TrailReviewOverflowMenu = ({
  isThirdPartyReview,
  isMoreOptionsOpen,
  setIsMoreOptionsOpen,
  onConnectClick,
  setIsReportingModalOpen,
  canComment,
  isEditable,
  isAdmin,
  hideReplies,
  onCommentClick,
  onDeleteClick,
  onEditClick,
  setReportingSuccessToast,
  summaryExclusion,
  onToggleSummaryExclusion,
  reviewUserId
}: TrailReviewOverflowMenuProps) => {
  const {
    formattedDefaultMessages: { MORE_OPTIONS, RESPOND, DELETE, EDIT, ADMIN, EXCLUDE_FROM_SUMMARY, ADD_TO_SUMMARY }
  } = useFormatMessage(messages);

  const onClickRemoveFromSummary = useMemo(() => {
    if (onToggleSummaryExclusion) {
      return () => {
        onToggleSummaryExclusion?.(true);
        setIsMoreOptionsOpen(false);
      };
    }
    return undefined;
  }, [onToggleSummaryExclusion, setIsMoreOptionsOpen]);

  const onClickAddToSummary = useMemo(() => {
    if (onToggleSummaryExclusion) {
      return () => {
        onToggleSummaryExclusion?.(false);
        setIsMoreOptionsOpen(false);
      };
    }
    return undefined;
  }, [onToggleSummaryExclusion, setIsMoreOptionsOpen]);

  const handleEditClick = useMemo(() => {
    if (onEditClick) {
      return () => {
        onEditClick?.();
        setIsMoreOptionsOpen(false);
      };
    }
    return undefined;
  }, [onEditClick, setIsMoreOptionsOpen]);

  return (
    <Popover
      placement="bottom-end"
      isOpen={isMoreOptionsOpen}
      className={styles.popover}
      onCloseRequest={() => setIsMoreOptionsOpen(false)}
      anchor={
        <div>
          <IconButtonWithTooltip
            icon={{ Component: Overflow }}
            onClick={() => setIsMoreOptionsOpen(!isMoreOptionsOpen)}
            testId="more-options"
            title={MORE_OPTIONS}
            uniqueId="trail-review-overflow-menu"
            variant="flat"
            size="sm"
          />
        </div>
      }
    >
      <div className={styles.menuList}>
        {isThirdPartyReview && isMoreOptionsOpen && (
          <UserConnectionOptions
            reviewUserId={reviewUserId}
            setIsReportingModalOpen={setIsReportingModalOpen}
            setReportingSuccessToast={setReportingSuccessToast}
            onConnectClick={onConnectClick}
          />
        )}
        {isEditable && (
          <div className={styles.adminOptions}>
            <div className={styles.divider} />
            {isAdmin && <div className={styles.adminItem}>{ADMIN}</div>}
            {onDeleteClick && (
              <LinkButtonOrDiv className={styles.menuItem} onClick={onDeleteClick} key={DELETE}>
                <span> {DELETE} </span>
              </LinkButtonOrDiv>
            )}
            {!hideReplies && canComment && onCommentClick ? (
              <LinkButtonOrDiv className={styles.menuItem} onClick={onCommentClick} key={RESPOND}>
                <span> {RESPOND} </span>
              </LinkButtonOrDiv>
            ) : (
              !isThirdPartyReview &&
              handleEditClick && (
                <LinkButtonOrDiv className={styles.menuItem} onClick={handleEditClick} key={EDIT}>
                  <span> {EDIT} </span>
                </LinkButtonOrDiv>
              )
            )}
            {isAdmin && !summaryExclusion && onClickRemoveFromSummary && (
              <LinkButtonOrDiv className={styles.menuItem} onClick={onClickRemoveFromSummary} key="Exclude from summary">
                <span> {EXCLUDE_FROM_SUMMARY} </span>
              </LinkButtonOrDiv>
            )}
            {isAdmin && summaryExclusion && onClickAddToSummary && (
              <LinkButtonOrDiv className={styles.menuItem} onClick={onClickAddToSummary} key="Add to summary">
                <span> {ADD_TO_SUMMARY} </span>
              </LinkButtonOrDiv>
            )}
          </div>
        )}
      </div>
    </Popover>
  );
};

export default TrailReviewOverflowMenu;
